import { validatePostSessionProvenance } from "./postSessionProvenance";

export const SUMMARY_GENERATION_STATES = Object.freeze([
  "draft",
  "final",
  "reviewed",
  "stale",
  "superseded",
]);

export const SUMMARY_DELIVERY_STATES = Object.freeze([
  "not_requested",
  "requested",
  "queued",
  "provider_accepted",
  "failed",
  "delivered",
]);

export const SUMMARY_AUDIT_PERSISTENCE_STATES = Object.freeze([
  "pending",
  "persisted",
  "failed",
]);

export const POST_SESSION_FINALIZATION_STATES = Object.freeze([
  "not_started",
  "pending",
  "ready",
  "failed",
]);

export const POST_SESSION_AUTOMATIC_EMAIL_STATES = Object.freeze([
  "not_started",
  "claiming",
  "queued",
  "sent",
  "failed",
]);

export const POST_SESSION_MANUAL_RECOVERY_LEASE_MS = 20 * 60 * 1000;

export const POST_SESSION_REMINDER_STATES = Object.freeze([
  "not_scheduled",
  "pending",
  "sent",
  "cancelled",
  "suppressed",
  "failed",
]);

const DELIVERY_STATUS_ALIASES = Object.freeze({
  accepted: "provider_accepted",
  provideraccepted: "provider_accepted",
  sent: "provider_accepted",
  attempting: "queued",
  pending: "queued",
  unknown: "queued",
  error: "failed",
  bounced: "failed",
  suppressed: "failed",
});

function normalizeText(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeStatus(value = "") {
  const normalized = normalizeText(value).toLowerCase().replace(/[\s-]+/g, "_");
  const compact = normalized.replace(/_/g, "");
  const aliased = DELIVERY_STATUS_ALIASES[normalized] || DELIVERY_STATUS_ALIASES[compact] || normalized;
  return SUMMARY_DELIVERY_STATES.includes(aliased) ? aliased : "";
}

function normalizeRetryCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
}

function normalizePersistenceStatus(value = "") {
  const normalized = normalizeText(value).toLowerCase().replace(/[\s-]+/g, "_");
  return SUMMARY_AUDIT_PERSISTENCE_STATES.includes(normalized) ? normalized : "";
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function statusToken(value = "") {
  return normalizeText(value).toLowerCase().replace(/[\s-]+/g, "_");
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return Number(value.toMillis()) || 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime() || 0;
  if (typeof value?.seconds === "number") return Number(value.seconds) * 1000;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizeSummaryGenerationStatus(value = "", hasSummary = true) {
  const normalized = normalizeText(value).toLowerCase().replace(/[\s-]+/g, "_");
  if (SUMMARY_GENERATION_STATES.includes(normalized)) return normalized;
  if (normalized === "available") return hasSummary ? "draft" : "";
  return hasSummary ? "draft" : "";
}

export function getSummaryGenerationLabel(value = "", hasSummary = true) {
  const status = normalizeSummaryGenerationStatus(value, hasSummary);
  if (status === "draft") return "Draft summary";
  if (status === "reviewed") return "Reviewed summary";
  if (status === "stale") return "Stale summary — not current";
  if (status === "superseded") return "Superseded summary";
  if (status === "final") return "Final summary";
  return "No summary generated";
}

export function normalizeSummaryDelivery(value = {}) {
  const raw = value && typeof value === "object" ? value : {};
  const explicitStatus = normalizeStatus(firstValue(raw.status, raw.deliveryStatus, raw.emailStatus));
  const requestedAt = firstValue(raw.requestedAt, raw.requestTimestamp) || null;
  const queuedAt = firstValue(raw.queuedAt, raw.queueTimestamp) || null;
  const providerAcceptedAt = firstValue(raw.providerAcceptedAt, raw.acceptedAt) || null;
  const providerMessageId = normalizeText(firstValue(raw.providerMessageId, raw.messageId));
  const deliveredAt = firstValue(raw.deliveredAt, raw.deliveryTimestamp) || null;
  const failedAt = firstValue(raw.failedAt, raw.failureTimestamp) || null;
  const bouncedAt = firstValue(raw.bouncedAt, raw.bounceTimestamp) || null;
  const suppressedAt = firstValue(raw.suppressedAt, raw.suppressionTimestamp) || null;
  const bounceState = normalizeText(firstValue(raw.bounceState, raw.bounceStatus, bouncedAt ? "bounced" : ""));
  const suppressionState = normalizeText(firstValue(raw.suppressionState, raw.suppressionStatus, suppressedAt ? "suppressed" : ""));
  const lastError = normalizeText(firstValue(raw.lastError, raw.error));
  const deliveryConfirmed = Boolean(
    raw.deliveryConfirmed === true ||
    raw.providerDeliveryConfirmed === true ||
    raw.deliveryConfirmation?.confirmed === true ||
    deliveredAt
  );
  const hasFailureEvidence = Boolean(
    explicitStatus === "failed" ||
    failedAt ||
    lastError ||
    bouncedAt ||
    suppressedAt ||
    bounceState ||
    suppressionState
  );
  const hasProviderAcceptanceEvidence = Boolean(
    explicitStatus === "provider_accepted" ||
    providerAcceptedAt ||
    providerMessageId
  );

  let status = "not_requested";
  if (hasFailureEvidence) status = "failed";
  else if (deliveryConfirmed) status = "delivered";
  else if (hasProviderAcceptanceEvidence || explicitStatus === "delivered") status = "provider_accepted";
  else if (explicitStatus === "queued" || queuedAt) status = "queued";
  else if (explicitStatus === "requested" || requestedAt) status = "requested";

  const attemptHistory = (Array.isArray(raw.attemptHistory) ? raw.attemptHistory : [])
    .map((attempt) => {
      const normalizedAttempt = normalizeSummaryDelivery({
        ...(attempt && typeof attempt === "object" ? attempt : {}),
        attemptHistory: [],
      });
      const { attemptHistory: ignoredHistory, ...evidence } = normalizedAttempt;
      return evidence;
    })
    .filter((attempt) => attempt.status !== "not_requested" || attempt.requestedAt);

  return {
    status,
    sourceSessionId: normalizeText(firstValue(raw.sourceSessionId, raw.sessionId)),
    requestingActorType: normalizeText(firstValue(raw.requestingActorType, raw.requestedByType, raw.actorType)),
    requestedAt,
    recipientIdentity: normalizeText(firstValue(raw.recipientIdentity, raw.recipientMasked)),
    queuedAt,
    providerAcceptedAt,
    providerAcceptanceEvidence: normalizeText(raw.providerAcceptanceEvidence),
    provider: normalizeText(firstValue(raw.provider, raw.emailProvider)),
    providerMessageId,
    deliveredAt,
    deliveryConfirmed: status === "delivered",
    failedAt,
    lastError,
    retryCount: normalizeRetryCount(firstValue(raw.retryCount, raw.retries)),
    bouncedAt,
    bounceState,
    suppressedAt,
    suppressionState,
    attemptHistory,
    persistenceStatus: normalizePersistenceStatus(raw.persistenceStatus),
    persistedAt: firstValue(raw.persistedAt, raw.auditPersistedAt) || null,
    persistenceFailedAt: firstValue(raw.persistenceFailedAt, raw.auditPersistenceFailedAt) || null,
    persistenceError: normalizeText(firstValue(raw.persistenceError, raw.auditPersistenceError)),
    updatedAt: firstValue(raw.updatedAt, deliveredAt, failedAt, providerAcceptedAt, queuedAt, requestedAt) || null,
  };
}

export function createSummaryDeliveryRequest(value = {}, request = {}) {
  const previous = normalizeSummaryDelivery(value);
  const requestedAt = request.requestedAt || new Date().toISOString();
  const previousAttempt = {
    ...previous,
  };
  delete previousAttempt.attemptHistory;
  const attemptHistory = [
    ...previous.attemptHistory,
    ...(
      previous.status !== "not_requested" || previous.requestedAt
        ? [previousAttempt]
        : []
    ),
  ];
  return normalizeSummaryDelivery({
    ...previous,
    ...request,
    status: "requested",
    requestedAt,
    retryCount: previous.status === "failed" ? previous.retryCount + 1 : previous.retryCount,
    queuedAt: null,
    providerAcceptedAt: null,
    providerAcceptanceEvidence: "",
    providerMessageId: "",
    messageId: "",
    deliveredAt: null,
    deliveryConfirmed: false,
    failedAt: null,
    lastError: "",
    bouncedAt: null,
    bounceState: "",
    suppressedAt: null,
    suppressionState: "",
    attemptHistory,
    persistenceStatus: "pending",
    persistedAt: null,
    persistenceFailedAt: null,
    persistenceError: "",
    updatedAt: requestedAt,
  });
}

export function getSummaryDeliveryLabel(value = {}) {
  const delivery = normalizeSummaryDelivery(value);
  if (delivery.status === "requested") return "Email requested";
  if (delivery.status === "queued") return "Email queued";
  if (delivery.status === "provider_accepted") return "Accepted by provider — delivery unconfirmed";
  if (delivery.status === "failed") return "Email delivery failed";
  if (delivery.status === "delivered") return "Delivered — provider confirmed";
  return "Email not requested";
}

export function getSummaryDeliveryDetail(value = {}) {
  const delivery = normalizeSummaryDelivery(value);
  if (delivery.persistenceStatus === "failed") {
    return delivery.persistenceError ||
      "The delivery outcome is visible locally, but its audit state could not be persisted.";
  }
  if (delivery.suppressionState || delivery.suppressedAt) {
    return "The provider reported suppression. Delivery did not complete.";
  }
  if (delivery.bounceState || delivery.bouncedAt) {
    return "The provider reported a bounce. Delivery did not complete.";
  }
  if (delivery.status === "requested") {
    return "The request is recorded but has not yet been queued.";
  }
  if (delivery.status === "queued") {
    return "The request reached the email relay queue; provider acceptance is not yet recorded.";
  }
  if (delivery.status === "provider_accepted") {
    return "The email provider accepted the message. Inbox delivery has not been confirmed.";
  }
  if (delivery.status === "failed") {
    return delivery.lastError || "The email request failed before confirmed delivery.";
  }
  if (delivery.status === "delivered") {
    return "The email provider confirmed delivery.";
  }
  return "No summary-email request has been recorded for this session.";
}

const FINALIZATION_ALIASES = {
  succeeded: "ready",
  reused: "ready",
  final: "ready",
  reviewed: "ready",
  stale: "failed",
  superseded: "failed",
  error: "failed",
};
const EMAIL_ALIASES = {
  not_requested: "not_started",
  requested: "claiming",
  pending: "claiming",
  dispatching: "claiming",
  queue_accepted: "queued",
  provider_accepted: "sent",
  delivered: "sent",
  accepted: "sent",
  ambiguous: "failed",
  bounced: "failed",
  error: "failed",
};
const REMINDER_ALIASES = {
  not_started: "not_scheduled",
  scheduled: "pending",
  dispatching: "pending",
  queue_accepted: "sent",
  provider_accepted: "sent",
  delivered: "sent",
  ambiguous: "failed",
  bounced: "failed",
  canceled: "cancelled",
};

function normalizeFinalization(value = {}) {
  const raw = asObject(value);
  const sourceStatus = statusToken(firstValue(
    raw.sourceStatus, raw.status, raw.summaryStatus, raw.generationStatus
  ));
  let status = FINALIZATION_ALIASES[sourceStatus] || sourceStatus || "not_started";
  if (!POST_SESSION_FINALIZATION_STATES.includes(status)) status = "not_started";
  if (firstValue(raw.lastError, raw.error) || raw.fallbackUsed === true) status = "failed";
  const terminalFailure = raw.terminalFailure === true || (
    raw.terminalFailure !== false &&
    ["failed", "stale", "superseded", "error"].includes(sourceStatus)
  );
  return {
    ...raw,
    status,
    sourceStatus,
    reused: sourceStatus === "reused",
    stale: ["stale", "superseded"].includes(sourceStatus),
    terminalFailure,
  };
}

function normalizeContinuationEmail(value = {}, trusted = true) {
  const raw = asObject(value);
  const sourceStatus = statusToken(firstValue(raw.status, raw.deliveryStatus, raw.emailStatus));
  const mapped = EMAIL_ALIASES[sourceStatus] || sourceStatus || "not_started";
  const ambiguous = sourceStatus === "ambiguous";
  let status = POST_SESSION_AUTOMATIC_EMAIL_STATES.includes(mapped) ? mapped : "not_started";
  if (sourceStatus === "bounced" || firstValue(raw.bouncedAt, raw.bounceState)) status = "failed";
  else if (ambiguous || raw.ambiguous === true || firstValue(raw.failedAt, raw.lastError, raw.error, raw.suppressedAt)) status = "failed";
  else if (
    firstValue(raw.deliveredAt, raw.deliveryConfirmed, raw.providerAcceptedAt, raw.providerMessageId, raw.messageId) ||
    (trusted && mapped === "sent")
  ) status = "sent";
  else if (firstValue(raw.queueAcceptedAt, raw.queuedAt) || mapped === "queued") status = "queued";
  else if (firstValue(raw.claimedAt, raw.dispatchAuthorizedAt, raw.pendingAt, raw.requestedAt) || mapped === "claiming") status = "claiming";
  return {
    ...raw,
    status,
    sourceStatus,
    ambiguous,
    attemptId: normalizeText(firstValue(raw.attemptId, raw.id, raw.idempotencyKey)),
  };
}

function normalizeReminder(value = {}) {
  const raw = asObject(value);
  const sourceStatus = statusToken(firstValue(raw.status, raw.reminderStatus));
  let status = REMINDER_ALIASES[sourceStatus] || sourceStatus || "not_scheduled";
  if (!POST_SESSION_REMINDER_STATES.includes(status)) status = "not_scheduled";
  if (firstValue(raw.cancelledAt, raw.canceledAt)) status = "cancelled";
  else if (raw.suppressedAt) status = "suppressed";
  else if (firstValue(raw.failedAt, raw.lastError, raw.reminderLastError)) status = "failed";
  else if (firstValue(raw.sentAt, raw.reminderSentAt, raw.queueAcceptedAt, raw.providerAcceptedAt, raw.deliveredAt)) status = "sent";
  else if (firstValue(raw.dueAt, raw.reminderAt, raw.scheduledFor, raw.pendingAt)) status = "pending";
  return {
    ...raw,
    status,
    sourceStatus,
    stage: sourceStatus || status,
    ambiguous: sourceStatus === "ambiguous",
  };
}

function normalizeManualResend(value = {}) {
  const raw = Array.isArray(value) ? { attempts: value } : asObject(value);
  const input = Array.isArray(raw.attempts)
    ? raw.attempts
    : Array.isArray(raw.attemptHistory)
      ? raw.attemptHistory
      : raw.status
        ? [raw]
        : [];
  const attempts = input.map((attempt) => normalizeContinuationEmail(attempt));
  return {
    attempts,
    attemptCount: attempts.length,
    lastAttemptAt: raw.lastAttemptAt || attempts[attempts.length - 1]?.updatedAt || null,
    latestAttempt: attempts[attempts.length - 1] || null,
  };
}

function directContinuation(record = {}) {
  const raw = asObject(record);
  if (Object.keys(asObject(raw.postSessionContinuation)).length) {
    return asObject(raw.postSessionContinuation);
  }
  const dedicated = [
    "finalization", "automaticEmail", "manualResend", "manualResends", "reminder", "completion",
  ].some((key) => Object.prototype.hasOwnProperty.call(raw, key));
  const summaryAlias = asObject(raw.summary).status;
  return dedicated || summaryAlias ? raw : null;
}

export function normalizePostSessionContinuation(value = {}, options = {}) {
  const record = asObject(value);
  const continuation = directContinuation(record);
  if (continuation) {
    const provenance = validatePostSessionProvenance(record);
    const requiresProvenance = Object.prototype.hasOwnProperty.call(
      record,
      "postSessionContinuation"
    );
    const manual = continuation.manualResend ||
      continuation.manualResends ||
      continuation.latestManualResend;
    const rawFinalization = normalizeFinalization(
      continuation.finalization || continuation.summary
    );
    const finalizationNeedsTrustedBinding =
      rawFinalization.status !== "not_started";
    const finalization = (
      requiresProvenance &&
      finalizationNeedsTrustedBinding &&
      (
        !provenance.finalizationBindingValid ||
        (
          rawFinalization.status === "ready" &&
          !provenance.trustedFinalSummary
        )
      )
    )
      ? {
          ...rawFinalization,
          status: "failed",
          ambiguous: true,
          provenanceMismatch: true,
          lastError:
            "The final-summary status does not match the authoritative session provenance.",
        }
      : rawFinalization;
    const rawAutomaticEmail = normalizeContinuationEmail(
      continuation.automaticEmail || continuation.automaticDelivery
    );
    const automaticEmail = (
      requiresProvenance &&
      rawAutomaticEmail.status !== "not_started" &&
      !provenance.automaticEmailBindingValid
    )
      ? {
          ...rawAutomaticEmail,
          status: "failed",
          ambiguous: true,
          lastError:
            "The automatic-email state does not match the authoritative summary version.",
        }
      : rawAutomaticEmail;
    const rawManualResend = normalizeManualResend(manual);
    const manualResend = (
      requiresProvenance &&
      rawManualResend.attemptCount > 0 &&
      !provenance.manualAttemptBindingsValid
    )
      ? {
          ...rawManualResend,
          attempts: rawManualResend.attempts.map((attempt) => ({
            ...attempt,
            status: "failed",
            ambiguous: true,
            lastError:
              "The manual-resend attempt does not match the authoritative summary version.",
          })),
          latestAttempt: rawManualResend.latestAttempt
            ? {
                ...rawManualResend.latestAttempt,
                status: "failed",
                ambiguous: true,
                lastError:
                  "The manual-resend attempt does not match the authoritative summary version.",
              }
            : null,
        }
      : rawManualResend;
    const rawReminder = normalizeReminder(continuation.reminder);
    const reminder = (
      requiresProvenance &&
      rawReminder.status !== "not_scheduled" &&
      !provenance.reminderBindingValid
    )
      ? {
          ...rawReminder,
          status: "failed",
          ambiguous: true,
          lastError:
            "The reminder state does not match the authoritative summary version.",
        }
      : rawReminder;
    return {
      source: "post_session_continuation",
      schemaVersion: Number(continuation.schemaVersion || 1),
      completion: asObject(continuation.completion),
      finalization,
      automaticEmail,
      manualResend,
      reminder,
      return: asObject(continuation.return),
      provenance,
    };
  }

  const summary = asObject(options.summary || record.sessionSummary || record.canonicalSummary);
  const summaryText = normalizeText(summary.summaryText || summary.reviewedSummaryText);
  const generation = normalizeSummaryGenerationStatus(
    firstValue(summary.generationStatus, summary.status),
    Boolean(summaryText)
  );
  const legacyCompleted = Boolean(
    record.sessionComplete === true ||
    record.sessionCompleted === true
  );
  const reliableLegacyFinal = Boolean(
    summaryText &&
    ["final", "reviewed"].includes(generation) &&
    summary.fallbackUsed !== true
  );
  const legacyProvenance = validatePostSessionProvenance(record);
  const versionedCompletionAwaitingWorker = Boolean(
    legacyCompleted &&
    normalizeText(asObject(record.completionVersion).id)
  );
  const finalizationStatus =
    versionedCompletionAwaitingWorker
      ? "pending"
      : summary.persistenceStatus === "failed" || ["stale", "superseded"].includes(generation)
      ? "failed"
      : reliableLegacyFinal
        ? "ready"
        : legacyProvenance.completionValid
          ? "pending"
        : legacyCompleted
          ? "failed"
        : summaryText || generation === "draft"
          ? "pending"
          : "not_started";
  const legacyDelivery = normalizeSummaryDelivery(
    options.delivery ||
    record.summaryDelivery ||
    record.sessionSummaryDelivery ||
    record.summaryEmailDelivery
  );
  const followUp = asObject(options.followUp || record.followUp);
  const followUpStatus = statusToken(firstValue(followUp.reminderStatus, followUp.status));
  let reminder = ["cancelled", "canceled", "dismissed"].includes(followUpStatus) ||
    firstValue(followUp.reminderCancelledAt, followUp.reminderCanceledAt)
    ? { ...followUp, status: "cancelled" }
    : followUp.reminderLastError
      ? { ...followUp, status: "failed" }
      : followUp.reminderSentAt
        ? { ...followUp, status: "sent" }
        : firstValue(followUp.reminderAt, followUp.reminderQueuedAt)
          ? { ...followUp, status: "pending" }
          : {};
  if (
    legacyCompleted &&
    !reliableLegacyFinal &&
    Object.keys(reminder).length &&
    !["cancelled", "suppressed"].includes(statusToken(reminder.status))
  ) {
    reminder = {
      ...reminder,
      status: "ambiguous",
      lastError: "legacy_reminder_has_no_current_final_summary_binding",
    };
  }
  const legacyInitialEmailAt = firstValue(
    record.initialEmailSentAt,
    asObject(record.reengagement).initialEmailSentAt
  );
  const legacyAutomaticEmail = (
    legacyDelivery.status === "not_requested" &&
    legacyInitialEmailAt
  )
    ? {
        status: "ambiguous",
        failedAt: legacyInitialEmailAt,
        lastError: "legacy_initialEmailSentAt_has_no_independent_acceptance_evidence",
      }
    : {
        ...legacyDelivery,
        status: legacyDelivery.status === "not_requested"
          ? "not_started"
          : legacyDelivery.status === "requested"
            ? "pending"
            : legacyDelivery.status,
      };
  return {
    source: "legacy",
    schemaVersion: null,
    completion: {},
    finalization: normalizeFinalization({
      ...summary,
      status: finalizationStatus,
      terminalFailure: false,
    }),
    automaticEmail: normalizeContinuationEmail(legacyAutomaticEmail, false),
    manualResend: normalizeManualResend(options.manualResend),
    reminder: normalizeReminder(reminder),
    return: {},
    provenance: legacyProvenance,
  };
}

export function getPostSessionFinalizationDisplayState(value = {}) {
  const record = asObject(value);
  const state = Object.keys(asObject(record.finalization)).length
    ? normalizeFinalization(record.finalization)
    : normalizeFinalization(record);
  const sourceStatus = statusToken(firstValue(state.sourceStatus, state.status));

  // A persisted pending state can briefly fail provenance validation while the
  // completion and continuation snapshots converge. That must remain a
  // processing state in the UI; it does not grant final-summary trust.
  if (sourceStatus === "pending" || state.status === "pending") return "pending";
  if (state.status === "ready" && state.ambiguous !== true) return "ready";
  if (
    state.status === "failed" &&
    state.ambiguous !== true &&
    state.terminalFailure === true &&
    ["failed", "stale", "superseded", "error"].includes(sourceStatus)
  ) {
    return "failed";
  }
  if (state.ambiguous === true || ["ready", "failed"].includes(state.status)) {
    return "unverified";
  }
  return "not_started";
}

export function canDisplayPostSessionFinalSummaryDetails(value = {}) {
  return getPostSessionFinalizationDisplayState(value) === "ready";
}

export function getPostSessionFinalizationLabel(value = {}) {
  const state = normalizeFinalization(value.finalization || value);
  const displayState = getPostSessionFinalizationDisplayState(value);
  if (displayState === "pending") return "Final summary is being prepared.";
  if (displayState === "ready") {
    return state.reused
      ? "Final summary ready — existing canonical summary reused"
      : "Final summary ready";
  }
  if (displayState === "failed") {
    return state.stale
      ? "Summary unavailable — stale result rejected"
      : "Summary failed or unavailable";
  }
  if (displayState === "unverified") return "Summary status unverified";
  return "Summary not started";
}

export function getPostSessionAutomaticEmailLabel(value = {}) {
  const state = value.automaticEmail || normalizeContinuationEmail(value);
  if (state.ambiguous) return "Automatic email status unverified";
  if (state.status === "claiming") return "Automatic email claim in progress";
  if (state.status === "queued") return "Automatic email queued — accepted for delivery";
  if (state.status === "sent") return "Automatic email sent — delivery unconfirmed";
  if (state.status === "failed") return "Automatic email failed";
  return "Automatic email not started";
}

export function getPostSessionManualResendEligibility(value = {}, options = {}) {
  const continuation = value?.source
    ? value
    : normalizePostSessionContinuation(value);
  if (continuation.finalization?.status !== "ready") {
    return { eligible: false, reason: "final_summary_not_ready" };
  }
  const hasAuthoritativeProvenance = continuation.provenance?.available === true;
  if (
    hasAuthoritativeProvenance &&
    continuation.provenance?.trustedFinalSummary !== true
  ) {
    return { eligible: false, reason: "final_summary_unverified" };
  }
  if (
    hasAuthoritativeProvenance &&
    continuation.provenance?.automaticEmailBindingValid !== true
  ) {
    return { eligible: false, reason: "automatic_email_unverified" };
  }

  const automaticEmail = continuation.automaticEmail || {};
  const status = automaticEmail.status || "not_started";
  if (["not_started", "failed"].includes(status)) {
    return { eligible: true, reason: `automatic_email_${status}` };
  }
  if (status === "claiming") {
    const nowMillis = timestampMillis(options.now || Date.now());
    const claimedAtMillis = timestampMillis(automaticEmail.claimedAt);
    const leaseMs = Number(
      options.leaseMs ?? POST_SESSION_MANUAL_RECOVERY_LEASE_MS
    );
    const expired = Boolean(
      nowMillis &&
      (
        !claimedAtMillis ||
        claimedAtMillis + Math.max(0, leaseMs) <= nowMillis
      )
    );
    return {
      eligible: expired,
      reason: expired
        ? "automatic_email_claim_expired"
        : "automatic_email_claiming",
    };
  }
  return { eligible: false, reason: `automatic_email_${status}` };
}

export function getPostSessionManualResendLabel(value = {}) {
  const latest = (value.manualResend || normalizeManualResend(value)).latestAttempt;
  const eligibility = getPostSessionManualResendEligibility(value);
  if (!latest && eligibility.reason === "final_summary_not_ready") {
    return "Manual resend unavailable until a final summary is ready";
  }
  if (!latest && eligibility.reason === "final_summary_unverified") {
    return "Manual resend unavailable — final summary status unverified";
  }
  if (!latest && eligibility.reason === "automatic_email_unverified") {
    return "Manual resend unavailable — automatic email status unverified";
  }
  if (!latest && eligibility.reason === "automatic_email_claiming") {
    return "Manual resend unavailable until the automatic attempt finishes";
  }
  if (!latest && eligibility.reason === "automatic_email_queued") {
    return "Manual resend unavailable while the automatic email is queued";
  }
  if (!latest && eligibility.reason === "automatic_email_sent") {
    return "Manual resend unavailable — automatic email already accepted";
  }
  if (!latest) {
    return eligibility.eligible
      ? "Manual resend available"
      : "Manual resend unavailable";
  }
  if (latest.ambiguous) return "Manual resend attempted — status unverified";
  if (latest.status === "claiming") return "Manual resend claim in progress";
  if (latest.status === "queued") return "Manual resend queued";
  if (latest.status === "sent") return "Manual resend sent — delivery unconfirmed";
  if (latest.status === "failed") return "Manual resend failed";
  return "Manual resend attempted";
}

export function getPostSessionReminderLabel(value = {}) {
  const reminder = value.reminder || normalizeReminder(value);
  if (reminder.ambiguous) return "Reminder status unverified";
  if (reminder.status === "pending") return reminder.stage === "scheduled"
    ? "Reminder scheduled"
    : "Reminder pending";
  if (reminder.status === "sent") {
    if (["queue_accepted", "queued"].includes(reminder.stage)) return "Reminder queue accepted";
    if (reminder.stage === "provider_accepted") return "Reminder provider accepted — delivery unconfirmed";
    if (reminder.stage === "delivered") return "Reminder delivered — provider confirmed";
    return "Reminder recorded as sent — delivery unconfirmed";
  }
  if (reminder.status === "cancelled") return "Reminder cancelled";
  if (reminder.status === "suppressed") return "Reminder suppressed";
  if (reminder.status === "failed") return "Reminder failed";
  return "Reminder not scheduled";
}

export function normalizeSessionLifecycleStatus(record = {}) {
  const raw = asObject(record);
  const provenance = validatePostSessionProvenance(raw);
  const completion = asObject(raw.postSessionContinuation).completion || {};
  const completionStatus = statusToken(firstValue(completion.status, raw.completionStatus));
  if (
    provenance.completionValid &&
    (
      raw.sessionComplete === true ||
      completion.sessionComplete === true ||
      completion.completed === true ||
      Boolean(completion.completionVersionId && completion.completedAt) ||
      ["complete", "completed", "succeeded"].includes(completionStatus)
    )
  ) return "completed";
  if (
    raw.sessionComplete === true ||
    raw.sessionCompleted === true ||
    firstValue(raw.endedAt, completion.endedAt, completion.completedAt)
  ) {
    return "closed_unverified";
  }
  return "active";
}

export function getSessionLifecycleLabel(record = {}) {
  const status = typeof record === "string"
    ? statusToken(record)
    : normalizeSessionLifecycleStatus(record);
  if (status === "completed") return "Completed";
  if (status === "closed_unverified") return "Closed — completion unverified";
  return "Active";
}
